import React, { useMemo, useState, useEffect } from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { FormControlLabel } from "@mui/material"
import { styled, Theme } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { CLOSE_FRIENDS, CREATE_LIST } from "constants/index"
import { PrivacyOption, privacyOptions } from "constants/options"
import { getPrivacyOptionDetail } from "utils/componentsUtils"
import { updateShowCreateNewListOverlay } from "appRedux/slices/itemFormSlice"
import { resetListFormSlice } from "appRedux/slices/listFormSlice"
import { useLazyGetWishListsQuery } from "appRedux/slices/wishListApiSlice"
import { useAppSelector } from "appRedux/hooks"
import useEventListener from "hooks/useEventListener"
import BottomSheet from "components/atoms/BottomSheet"
import SearchInput from "components/atoms/SearchInput"
import CTA from "components/atoms/CTA"
import Divider from "components/atoms/Divider"
import Checkbox from "components/atoms/Checkbox"
import withPremiumPermission from "components/HOCs/withPremiumPermission"

type SelectListDropSheetToAddItemsProps = {
  show: boolean;
  defaultSelectedLists: number[];
  CTALabel?: string;
  onOpen: () => void;
  onClose: () => void;
  onSubmit: (selectedListsIds: number[]) => void;
}

export default function SelectListDropSheetToAddItems(props: SelectListDropSheetToAddItemsProps) {
  const { show, defaultSelectedLists, CTALabel, onOpen, onClose, onSubmit } = props

  const isPremium = useAppSelector((state) => state.user.profile?.isPremium)

  const [selectedListsIds, setSelectedListsIds] = useState<number[]>([])
  const [localUserWishLists, setLocalUserWishLists] = useState<WishList[]>([])

  const userWishLists = useAppSelector((state) => state.user.wishLists)

  const [triggerGetWishLists] = useLazyGetWishListsQuery()

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const privacyOptionDetails = useMemo(() => {
    const privacyMap = new Map()

    privacyOptions.forEach((option: PrivacyOption) => {
      if (!privacyMap.has(option.key)) {
        privacyMap.set(option.key, getPrivacyOptionDetail(option.key))
      }
    })

    return privacyMap
  }, [])

  useEventListener(document, "select-list", selectNewLyCreatedList)

  useEffect(() => {
    if (!show) return
    setSelectedListsIds(defaultSelectedLists)
  }, [show])

  useEffect(() => {
    if (!show) return
    if (userWishLists?.length) {
      const lists = !isPremium ? userWishLists.filter(list => list.privacy !== CLOSE_FRIENDS) : userWishLists
      setLocalUserWishLists(lists)
    } else {
      setLocalUserWishLists([])
      triggerGetWishLists()
    }
  }, [show, userWishLists])

  function selectNewLyCreatedList(event: any) {
    const { id } = event.detail
    setSelectedListsIds([...selectedListsIds, id])
  }

  function renderBottomSheetTitle() {
    return (
      <Stack width="100%" flexDirection="row" alignItems="center" justifyContent="space-between">
        <div>{t("items.listSelection")}</div>
        <div>
          <CreateNewListCTAWithPremiumPermission
            feature={CREATE_LIST}
            variant={"text"}
            onClick={handleCreateNewList}
          >
            {t("collections.createNewList")}
          </CreateNewListCTAWithPremiumPermission>
        </div>
      </Stack>
    )
  }

  function handleCreateNewList() {
    dispatch(resetListFormSlice())
    dispatch(updateShowCreateNewListOverlay(true))
  }

  function handleOnListCheckChange(event: React.SyntheticEvent, listId: number) {
    // @ts-ignore
    const isChecked = event.target.checked
    if (isChecked) {
      setSelectedListsIds(prevState => [...prevState, listId])
    } else {
      setSelectedListsIds(prevState => prevState.filter(id => id !== listId))
    }
  }

  function handleSearchListName(event: React.SyntheticEvent) {
    // @ts-ignore
    const searchedValue = event.target.value
    if (!userWishLists) return
    const filteredLists = userWishLists.filter(list => list.name.includes(searchedValue))
    setLocalUserWishLists(filteredLists)
  }

  function handleSelectLists() {
    onSubmit(selectedListsIds)
  }

  function submitButtonIsDisabled() {
    if (!defaultSelectedLists) return
    const sortedDefaultSelectedLists = [...defaultSelectedLists].sort((a, b) => a - b)
    const sortedSelectedLists = [...selectedListsIds].sort((a, b) => a - b)
    return JSON.stringify(sortedDefaultSelectedLists) === JSON.stringify(sortedSelectedLists)
  }

  return (
    <BottomSheet
      id="select-list-bottom-sheet"
      title={renderBottomSheetTitle()}
      show={show}
      height={"60vh"}
      openBottomSheet={onOpen}
      closeBottomSheet={onClose}
    >
      <Stack height="calc(100% - 142px)" marginBottom={"8px"}>
        <SearchInput
          id={"search-list-input"}
          placeholder={t("collections.searchListName")}
          onChange={handleSearchListName}
        />
        <ListsContainer>
          {localUserWishLists.map((list, index) => (
            <Stack key={list.id} gap={"4px"}>
              <List isselected={selectedListsIds && selectedListsIds.includes(list.id) ? 1 : 0}>
                <StyledFormControlLabel
                  control={<Checkbox variant="add"/>}
                  checked={selectedListsIds && selectedListsIds.includes(list.id)}
                  label={
                    <RightSide>
                      {privacyOptionDetails.get(list.privacy).filledIcon}
                      <Name>{list.name}</Name>
                      <ItemsCount>{list.itemCount} {t("items.name")}</ItemsCount>
                    </RightSide>
                  }
                  onChange={(event) => handleOnListCheckChange(event, list.id)}
                />
              </List>
              {index < localUserWishLists.length - 1 && (<Divider/>)}
            </Stack>
          ))}
        </ListsContainer>
      </Stack>
      <CTA
        variant="contained"
        type="submit"
        disabled={submitButtonIsDisabled()}
        onClick={handleSelectLists}
      >
        {CTALabel || t("general.select")}
      </CTA>
    </BottomSheet>
  )
}

const ListsContainer = styled(Stack)(({ theme }) => ({
  gap: "4px",
  marginTop: "-32px",
  overflow: "scroll"
}))

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  width: "100%",
  flexDirection: "row-reverse",
  justifyContent: "space-between",
  margin: 0
}))

type ListProps = {
  theme?: Theme;
  isselected: 1 | 0
}

const List = styled(Stack)(({ theme, isselected }: ListProps) => ({
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  borderRadius: "8px",
  padding: "12px 8px",
  backgroundColor: isselected ? theme?.palette.primary["1"] : "transparent",
  transition: "0.2s"
}))

const RightSide = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  gap: "8px",

  "& svg": {
    width: "20px",
    height: "20px"
  }
}))

const Name = styled(Typography)(({ theme }) => ({
  fontSize: "1.2rem",
  color: theme.palette.gray["15"]
}))

const ItemsCount = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  color: theme.palette.gray["12"],
  fontFamily: "VazirmatnFD, serif !important"
}))

const CreateNewListCTAWithPremiumPermission = withPremiumPermission(CTA)
